import React, { useState, useRef, useEffect } from 'react'
import axios from 'axios'
import { gql } from 'graphql-request'
import { Upload, Trash2, ImageIcon, Video, Save, RotateCcw, CreditCard } from 'lucide-react'
import { Button } from '@/components/ui/button'
import LoadingSpinner from '@/components/Common/LoadingSpinner'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import type { OrganisationWithDetails } from '../index'
import { PAYMENT_ACCOUNT_FIELDS, type PaymentAccount } from '@/pages/PaymentAccounts'
import LoginPageSection from './LoginPageSection'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4050'

const UPDATE_ONBOARDING_VIDEOS = gql`
  mutation updateOrganisationOnboardingVideos($organisationId: Int!, $onboardingVideo1Url: String, $onboardingVideo2Url: String) {
    updateOrganisationOnboardingVideos(organisationId: $organisationId, onboardingVideo1Url: $onboardingVideo1Url, onboardingVideo2Url: $onboardingVideo2Url) {
      id
      onboardingVideo1Url
      onboardingVideo2Url
    }
  }
`

const GET_PAYMENT_ACCOUNTS = gql`
  query getPaymentAccounts {
    getPaymentAccounts {
      ${PAYMENT_ACCOUNT_FIELDS}
    }
  }
`

const SET_ORGANISATION_PAYMENT_ACCOUNT = gql`
  mutation setOrganisationPaymentAccount($organisationId: Int!, $paymentAccountId: Int, $applicationFeeBps: Int) {
    setOrganisationPaymentAccount(organisationId: $organisationId, paymentAccountId: $paymentAccountId, applicationFeeBps: $applicationFeeBps) {
      id
      paymentAccountId
      applicationFeeBps
      paymentsUsable
    }
  }
`

/** Why an account cannot be used, in words an administrator can act on. */
const UNUSABLE_REASON: Record<string, string> = {
  archived: 'archived',
  disconnected: 'disconnected from Georgia',
  restricted: 'restricted by Stripe',
  onboarding: 'onboarding not finished',
  wrong_mode: 'belongs to the other Stripe mode',
}

interface SettingsTabProps {
  organisation: OrganisationWithDetails | null
  loading: boolean
  onRefresh?: () => void
}

const SettingsTab: React.FC<SettingsTabProps> = ({ organisation, loading, onRefresh }) => {
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [logoKey, setLogoKey] = useState(0)
  const [hasLogo, setHasLogo] = useState(true)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Payments, B2C only. An organisation has at most one payment account; one account
  // may serve several organisations.
  const [paymentAccounts, setPaymentAccounts] = useState<PaymentAccount[]>([])
  const [selectedAccountId, setSelectedAccountId] = useState('')
  const [feePercent, setFeePercent] = useState('')
  const [savingPayments, setSavingPayments] = useState(false)
  const [paymentsError, setPaymentsError] = useState<string | null>(null)
  const [paymentsSuccess, setPaymentsSuccess] = useState<string | null>(null)

  const [video1Url, setVideo1Url] = useState('')
  const [video2Url, setVideo2Url] = useState('')
  const [savingVideos, setSavingVideos] = useState(false)
  const [videoError, setVideoError] = useState<string | null>(null)
  const [videoSuccess, setVideoSuccess] = useState<string | null>(null)

  const logoUrl = organisation ? `${API_URL}/api/organisations/${organisation.id}/logo` : null

  useEffect(() => {
    setHasLogo(true)
    setLogoKey((k) => k + 1)
    setError(null)
    setSuccess(null)
    setVideo1Url((organisation as any)?.onboardingVideo1Url || '')
    setVideo2Url((organisation as any)?.onboardingVideo2Url || '')
    setVideoError(null)
    setVideoSuccess(null)
    setSelectedAccountId(organisation?.paymentAccountId ? String(organisation.paymentAccountId) : '')
    // Basis points in the database, a percentage in the form.
    setFeePercent(organisation?.applicationFeeBps != null ? String(organisation.applicationFeeBps / 100) : '')
    setPaymentsError(null)
    setPaymentsSuccess(null)
  }, [organisation?.id])

  const isB2c = organisation?.kind === 'b2c'

  useEffect(() => {
    if (!isB2c) return
    let cancelled = false
    authorizedGraphQLRequest<{ getPaymentAccounts: PaymentAccount[] }>(GET_PAYMENT_ACCOUNTS)
      .then((response) => {
        if (!cancelled) setPaymentAccounts(response.getPaymentAccounts || [])
      })
      .catch((err) => console.error('Failed to load payment accounts:', err))
    return () => {
      cancelled = true
    }
  }, [isB2c])

  const handleSavePayments = async () => {
    if (!organisation) return
    setPaymentsError(null)
    setPaymentsSuccess(null)

    let applicationFeeBps: number | null = null
    if (feePercent.trim() !== '') {
      const percent = Number(feePercent)
      if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
        setPaymentsError('The fee must be a percentage between 0 and 100, or empty for none.')
        return
      }
      applicationFeeBps = Math.round(percent * 100)
    }

    try {
      setSavingPayments(true)
      await authorizedGraphQLRequest(SET_ORGANISATION_PAYMENT_ACCOUNT, {
        organisationId: organisation.id,
        paymentAccountId: selectedAccountId === '' ? null : Number(selectedAccountId),
        applicationFeeBps,
      })
      setPaymentsSuccess('Payment settings saved.')
      // Reload the organisation in place, so `paymentsUsable` and the warning on the
      // Modules tab reflect the account that was just attached.
      onRefresh?.()
    } catch (err: any) {
      setPaymentsError(err?.response?.errors?.[0]?.message || 'Failed to save the payment settings')
    } finally {
      setSavingPayments(false)
    }
  }

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token')
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !organisation) return

    setError(null)
    setSuccess(null)

    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError('Invalid file type. Only PNG and JPEG are accepted.')
      return
    }

    if (file.size > 200 * 1024) {
      setError('File too large. Maximum size is 200KB.')
      return
    }

    const formData = new FormData()
    formData.append('logo', file)

    try {
      setUploading(true)
      await axios.post(`${API_URL}/api/organisations/${organisation.id}/logo`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      })
      setHasLogo(true)
      setLogoKey((k) => k + 1)
      setSuccess('Logo uploaded successfully.')
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Failed to upload logo.'
      setError(message)
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleRemove = async () => {
    if (!organisation) return
    if (!confirm('Remove the organisation logo?')) return

    setError(null)
    setSuccess(null)

    try {
      setDeleting(true)
      await axios.delete(`${API_URL}/api/organisations/${organisation.id}/logo`, {
        headers: getAuthHeaders(),
      })
      setHasLogo(false)
      setSuccess('Logo removed.')
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Failed to remove logo.'
      setError(message)
    } finally {
      setDeleting(false)
    }
  }

  const handleSaveVideos = async () => {
    if (!organisation) return

    setVideoError(null)
    setVideoSuccess(null)
    setSavingVideos(true)

    try {
      await authorizedGraphQLRequest(UPDATE_ONBOARDING_VIDEOS, {
        organisationId: organisation.id,
        onboardingVideo1Url: video1Url || null,
        onboardingVideo2Url: video2Url || null,
      })
      setVideoSuccess('Onboarding video URLs saved successfully.')
    } catch (err: any) {
      setVideoError(err?.response?.errors?.[0]?.message || 'Failed to save video URLs.')
    } finally {
      setSavingVideos(false)
    }
  }

  const handleResetVideos = async () => {
    if (!organisation) return
    if (!confirm('Reset onboarding videos to platform defaults?')) return

    setVideoError(null)
    setVideoSuccess(null)
    setSavingVideos(true)

    try {
      await authorizedGraphQLRequest(UPDATE_ONBOARDING_VIDEOS, {
        organisationId: organisation.id,
        onboardingVideo1Url: null,
        onboardingVideo2Url: null,
      })
      setVideo1Url('')
      setVideo2Url('')
      setVideoSuccess('Onboarding videos reset to defaults.')
    } catch (err: any) {
      setVideoError(err?.response?.errors?.[0]?.message || 'Failed to reset video URLs.')
    } finally {
      setSavingVideos(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!organisation) {
    return <div className="text-center text-gray-500 py-12">No organisation selected</div>
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h4 className="font-semibold text-gray-800 mb-4">Organisation Logo</h4>
        <p className="text-sm text-gray-500 mb-6">
          Upload a logo for this organisation. It is displayed in the sidebar of Module 2 and on the sign-in page people reach through this organisation's join links. Accepted formats: PNG, JPEG.
          Maximum size: 200KB.
        </p>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-4">{error}</div>}
        {success && <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm mb-4">{success}</div>}

        <div className="flex items-start gap-6">
          {/* Logo Preview */}
          <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden shrink-0">
            {hasLogo && logoUrl ? (
              <img key={logoKey} src={`${logoUrl}?v=${logoKey}`} alt="Organisation logo" className="max-w-full max-h-full object-contain" onError={() => setHasLogo(false)} />
            ) : (
              <ImageIcon className="h-8 w-8 text-gray-400" />
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <div>
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg" onChange={handleUpload} className="hidden" id="logo-upload" />
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploading || deleting} variant="outline" className="border-gray-300">
                {uploading ? <LoadingSpinner size="sm" /> : <Upload className="h-4 w-4 mr-2" />}
                {uploading ? 'Uploading...' : 'Choose File'}
              </Button>
            </div>
            {hasLogo && (
              <Button onClick={handleRemove} disabled={uploading || deleting} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                {deleting ? <LoadingSpinner size="sm" /> : <Trash2 className="h-4 w-4 mr-2" />}
                {deleting ? 'Removing...' : 'Remove Logo'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <LoginPageSection organisationId={organisation.id} organisationName={organisation.name} logoUrl={hasLogo && logoUrl ? `${logoUrl}?v=${logoKey}` : null} />

      {isB2c && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="h-5 w-5 text-gray-700" />
            <h4 className="font-semibold text-gray-800">Payments</h4>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            The partner's Stripe account receives the money for every purchase in this organisation. They are the seller and handle their own tax. Create and onboard accounts in Payment accounts.
          </p>

          {paymentsError && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-4">{paymentsError}</div>}
          {paymentsSuccess && <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm mb-4">{paymentsSuccess}</div>}

          <div className="space-y-4">
            <div>
              <label htmlFor="payment-account" className="block text-sm font-medium text-gray-700 mb-1">
                Payment account
              </label>
              <select
                id="payment-account"
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                disabled={savingPayments}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">None — users cannot buy minutes</option>
                {paymentAccounts
                  .filter((account) => account.isActive || account.id === organisation?.paymentAccountId)
                  .map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.displayName}
                      {account.usable ? '' : ` — not usable (${UNUSABLE_REASON[account.unusableReason ?? ''] ?? 'unavailable'})`}
                    </option>
                  ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {organisation?.paymentsUsable
                  ? 'Users of this organisation can buy minutes for any module that has active packages.'
                  : 'Until a usable account is attached, the offer modal shows no purchase action.'}
              </p>
            </div>

            <div>
              <label htmlFor="application-fee" className="block text-sm font-medium text-gray-700 mb-1">
                Georgia's fee (%)
              </label>
              <input
                id="application-fee"
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={feePercent}
                onChange={(e) => setFeePercent(e.target.value)}
                placeholder="None"
                disabled={savingPayments}
                className="w-32 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <p className="mt-1 text-xs text-gray-500">Leave empty to take nothing. Applies to purchases made from now on.</p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleSavePayments} disabled={savingPayments} variant="outline" className="border-gray-300">
                {savingPayments ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4 mr-2" />}
                {savingPayments ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Video className="h-5 w-5 text-gray-700" />
          <h4 className="font-semibold text-gray-800">Onboarding Videos</h4>
        </div>
        <p className="text-sm text-gray-500 mb-6">Set custom YouTube video URLs for the onboarding flow. Leave empty to use platform defaults.</p>

        {videoError && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-4">{videoError}</div>}
        {videoSuccess && <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm mb-4">{videoSuccess}</div>}

        <div className="space-y-4">
          <div>
            <label htmlFor="video1-url" className="block text-sm font-medium text-gray-700 mb-1">
              Video 1 — Getting Started
            </label>
            <input
              id="video1-url"
              type="text"
              value={video1Url}
              onChange={(e) => setVideo1Url(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              disabled={savingVideos}
            />
          </div>
          <div>
            <label htmlFor="video2-url" className="block text-sm font-medium text-gray-700 mb-1">
              Video 2 — Video Tutorial
            </label>
            <input
              id="video2-url"
              type="text"
              value={video2Url}
              onChange={(e) => setVideo2Url(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              disabled={savingVideos}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button onClick={handleSaveVideos} disabled={savingVideos} variant="outline" className="border-gray-300">
              {savingVideos ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4 mr-2" />}
              {savingVideos ? 'Saving...' : 'Save'}
            </Button>
            <Button onClick={handleResetVideos} disabled={savingVideos} variant="outline" className="border-gray-300 text-gray-600">
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SettingsTab
